// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-floating-promises, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, react-hooks/exhaustive-deps, react-hooks/rules-of-hooks */

import React, { FC, useEffect, useState, useMemo } from "react";
import { Button, Form, Modal } from "semantic-ui-react";
import * as excelGenerator from "../../../apis/excelGeneratorApi.js";
import * as excelGenerationFunctions from "../../../utilities/excelGeneration.js";
import * as protocolOptions from "../../../apis/protocolApi.js";
import { useAuth0 } from "../../../auth/auth0";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useUserAuth } from "../../../hooks/useUserAuth";
import { useModuleNavigation } from "../../../hooks/useModuleNavigation.js";
import _ from "lodash";
import moment from "moment";
import { useSelector } from "react-redux";

enum ExportTypes {
	Universal = "Universal",
	Tabbed = "Tabbed",
	Standard = "Standard",
	PlotGPSCoords = "PlotGPSCoords",
}

type Props = {
	id: string;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	name?: string;
	type: "Trial" | "Field" | "Protocol";
};

const dataDownload: {
	id: string;
	includeSubplots: boolean;
	quantifiedRegion: string;
	isForAdmin: boolean;
	exclusions: boolean;
	isForCSV: boolean;
	currentDateTimeString: string;
} = {
	id: "",
	includeSubplots: false,
	quantifiedRegion: "",
	isForAdmin: false,
	exclusions: false,
	isForCSV: false,
	currentDateTimeString: JSON.stringify(new Date().toLocaleString()),
};

const HeatmapExportModal: FC<Props> = ({ id, open, setOpen, name, type }) => {
	const { getTokenSilently } = useAuth0();
	const userAuth = useUserAuth();
	const moduleNavigation = useModuleNavigation();
	const queryClient = useQueryClient();
	const entityNames = useSelector((state) => (state.entityNames ? state.entityNames : null));

	//Dropdowns
	const [fileFormatOptions, setFileFormatOptions] = useState([] as any[]);
	const [dataUnitOptions, setDataUnitOptions] = useState([] as any[]);
	const [filteredDataUnitOptions, setFilteredDataUnitOptions] = useState([] as any[]);
	const [quantifiedRegionOptions, setQuantifiedRegionOptions] = useState([] as any[]);
	const [filteredQuantifiedRegionOptions, setFilteredQuantifiedRegionOptions] = useState([] as any[]);
	const [dataContentOptions, setDataContentOptions] = useState([] as any[]);
	const [filteredDataContentOptions, setFilteredDataContentOptions] = useState([] as any[]);

	//Selected Values
	const [fileFormat, setFileFormat] = useState<string | undefined>(undefined);
	const [dataUnit, setDataUnit] = useState<string | undefined>(undefined);
	const [quantifiedRegion, setQuantifiedRegion] = useState<string | undefined>(undefined);
	const [dataContent, setDataContent] = useState<string | undefined>(undefined);

	const [localEntityName, setLocalEntityName] = useState(name);
	const [dashboardOptions, setDashboardOptions] = useState(null);

	const entityName = useMemo(() => {
		return type === "Trial"
			? entityNames?.trialName
			: type === "Field"
				? entityNames?.fieldName
				: entityNames?.protocolName;
	}, [entityNames, type]);

	const isFieldScaleTrial = useMemo(() => {
		return entityNames?.fieldScaleTrial;
	}, [entityNames]);

	const isApAdmin = userAuth.isApAdmin;

	useEffect(() => {
		setupFileFormatOptions();
		setupDataUnitOptions();
		setupQuantifiedRegionOptions();
		setupDataContentOptions();
	}, [isApAdmin, isFieldScaleTrial]);

	useEffect(() => {
		if (entityName) {
			setLocalEntityName(entityName);
		}
	}, [entityName]);

	//Used to get trial ids for Protocol downloads
	useEffect(() => {
		if (type === "Protocol" && !dashboardOptions) {
			protocolDashboardOptionsMutate();
		}
	}, [type, id]);

	const setupFileFormatOptions = () => {
		const options = [
			{
				key: "tabbedDataFrame",
				text: "Tabbed Data Frames (.xlsx)",
				value: "tabbedDataFrame",
				isForCSV: false,
				type: ExportTypes.Tabbed,
				exclusions: true,
				hidden: isFieldScaleTrial,
				isForStackedDownload: true,
			},
			{
				key: "universalDataFrame",
				text: "Universal Data Frames (.csv)",
				value: "universalDataFrame",
				isForCSV: true,
				type: ExportTypes.Universal,
				exclusions: true,
				hidden: false,
				isForStackedDownload: false,
			},
			{
				key: "universalDataFrameExcel",
				text: "Universal Data Frames (.xlsx)",
				value: "universalDataFrameExcel",
				isForCSV: false,
				type: ExportTypes.Universal,
				exclusions: true,
				hidden: false,
				isForStackedDownload: false,
			},
			{
				key: "standardDataFrame",
				text: "Standard (.xlsx)",
				value: "standardDataFrame",
				isForCSV: false,
				type: ExportTypes.Standard,
				exclusions: true,
				hidden: false,
				isForStackedDownload: false,
			},
			{
				key: "standardNoExclusionsDataFrame",
				text: "Standard Without Exclusions (.xlsx)",
				value: "standardNoExclusionsDataFrame",
				isForCSV: false,
				type: ExportTypes.Standard,
				exclusions: false,
				hidden: false,
				isForStackedDownload: false,
			},
			{
				key: "plotGPSCoords",
				text: "Plot GPS Coordinates (.xlsx)",
				value: "plotGPSCoords",
				isForCSV: false,
				type: ExportTypes.PlotGPSCoords,
				exclusions: false,
				hidden: !isApAdmin,
				isForStackedDownload: false,
			},
		];

		setFileFormatOptions(_.filter(options, (o) => !o.hidden));
		setFileFormat(options[3].value);
	};

	const setupDataUnitOptions = () => {
		const options = [
			{ key: "plots", text: "Plots", value: "plots", hidden: false, includeSubplots: false },
			{ key: "subplots", text: "Subplots (Admin Only)", value: "subplots", hidden: !isApAdmin, includeSubplots: true },
		];

		setDataUnitOptions(_.filter(options, (o) => !o.hidden));
		setFilteredDataUnitOptions(_.filter(options, (o) => !o.hidden && o.value !== "subplots"));
		setDataUnit(options[0].value);
	};

	const setupQuantifiedRegionOptions = () => {
		const options = [
			{ key: "standard", text: "Default Qr", value: "standard", hidden: false },
			{ key: "all", text: "All Qrs", value: "all", hidden: false },
		];

		setQuantifiedRegionOptions(_.filter(options, (o) => !o.hidden));
		setFilteredQuantifiedRegionOptions(_.filter(options, (o) => !o.hidden && o.value !== "all"));
		setQuantifiedRegion(options[0].value);
	};

	const setupDataContentOptions = () => {
		const options = [
			{ key: "standard", text: "Standard Data", value: "standard", hidden: false, isForAdmin: false },
			{
				key: "all",
				text: "All Data (Admin Only)",
				value: "all",
				hidden: !isApAdmin || type === "Field",
				isForAdmin: true,
			},
		];

		setDataContentOptions(_.filter(options, (o) => !o.hidden));
		setFilteredDataContentOptions(_.filter(options, (o) => !o.hidden));
		setDataContent(options[0].value);
	};

	const resetInputs = () => {
		setFileFormat(fileFormatOptions[2].value);
		setDataUnit(dataUnitOptions[0].value);
		setQuantifiedRegion(quantifiedRegionOptions[0].value);
		setDataContent(dataContentOptions[0].value);
	};

	// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
	const filterDataUnitOptions = (value) => {
		const match = fileFormatOptions.find((x) => x.value == value);
		if (match.type == ExportTypes.Standard) {
			setFilteredDataUnitOptions(_.filter(dataUnitOptions, (o) => o.value !== "subplots"));
			setDataUnit(dataUnitOptions[0].value);
		} else {
			setFilteredDataUnitOptions(dataUnitOptions);
		}
	};

	// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
	const filterQuantifiedRegionOptions = (value) => {
		const match = fileFormatOptions.find((x) => x.value == value);
		if (
			match?.type == ExportTypes.Standard ||
			(match?.type == ExportTypes.PlotGPSCoords && dataUnit == "plots") ||
			(match == undefined && value == "plots")
		) {
			setFilteredQuantifiedRegionOptions(_.filter(quantifiedRegionOptions, (o) => o.value !== "all"));
			setQuantifiedRegion(quantifiedRegionOptions[0].value);
		} else {
			setFilteredQuantifiedRegionOptions(quantifiedRegionOptions);
		}
	};

	// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
	const filterDataContentOptions = (value) => {
		const match = fileFormatOptions.find((x) => x.value == value);
		if (match.type == ExportTypes.PlotGPSCoords) {
			setFilteredDataContentOptions(_.filter(dataContentOptions, (o) => o.value !== "all"));
			setDataContent(dataContentOptions[0].value);
		} else {
			setFilteredDataContentOptions(dataContentOptions);
		}
	};

	const generateStandardTrialHeatmapExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generateTrialHeatmapExcel(
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.id,
			vars.isForAdmin,
			vars.currentDateTimeString,
			vars.exclusions,
			accessToken,
		);
	};

	const generateStandardProtocolHeatmapExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generateProtocolHeatmapExcel(
			(dashboardOptions as any).trialIds,
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.isForAdmin,
			vars.currentDateTimeString,
			vars.exclusions,
			accessToken,
		);
	};

	const generateStackedTrialHeatmapExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generateStackedTrialHeatmapExcel(
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.id,
			vars.isForAdmin,
			vars.currentDateTimeString,
			vars.exclusions,
			vars.quantifiedRegion,
			vars.includeSubplots,
			vars.isForCSV,
			accessToken,
		);
	};

	const generateTabbedTrialHeatmapExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generateTabbedTrialHeatmapExcel(
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.id,
			vars.isForAdmin,
			vars.currentDateTimeString,
			vars.exclusions,
			vars.quantifiedRegion,
			vars.includeSubplots,
			vars.isForCSV,
			accessToken,
		);
	};

	const generatePlotGPSTrialCoordsExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generatePlotGPSTrialCoordsExcel(
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.id,
			vars.currentDateTimeString,
			vars.quantifiedRegion,
			vars.includeSubplots,
			accessToken,
		);
	};

	const generateStackedProtocolHeatmapExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generateStackedProtocolHeatmapExcel(
			(dashboardOptions as any).trialIds,
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.isForAdmin,
			vars.currentDateTimeString,
			vars.exclusions,
			vars.quantifiedRegion,
			vars.includeSubplots,
			vars.isForCSV,
			accessToken,
		);
	};

	const generateTabbedProtocolHeatmapExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generateTabbedProtocolHeatmapExcel(
			(dashboardOptions as any).trialIds,
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.isForAdmin,
			vars.currentDateTimeString,
			vars.exclusions,
			vars.quantifiedRegion,
			vars.includeSubplots,
			vars.isForCSV,
			accessToken,
		);
	};

	const generatePlotGPSProtocolCoordsExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generatePlotGPSProtocolCoordsExcel(
			(dashboardOptions as any).trialIds,
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.currentDateTimeString,
			vars.quantifiedRegion,
			vars.includeSubplots,
			accessToken,
		);
	};

	const generateStandardFieldHeatmapExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generateFieldHeatmapExcel(
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.id,
			vars.currentDateTimeString,
			vars.exclusions,
			accessToken,
		);
	};

	const generateStackedFieldHeatmapExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generateStackedFieldHeatmapExcel(
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.id,
			vars.currentDateTimeString,
			vars.exclusions,
			vars.quantifiedRegion,
			vars.includeSubplots,
			vars.isForCSV,
			accessToken,
		);
	};

	const generateTabbedFieldHeatmapExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generateTabbedFieldHeatmapExcel(
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.id,
			vars.currentDateTimeString,
			vars.exclusions,
			vars.quantifiedRegion,
			vars.includeSubplots,
			vars.isForCSV,
			accessToken,
		);
	};

	const generatePlotGPSFieldCoordsExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generatePlotGPSFieldCoordsExcel(
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.id,
			vars.currentDateTimeString,
			vars.quantifiedRegion,
			vars.includeSubplots,
			accessToken,
		);
	};

	const getProtocolDashboardOptions = async () => {
		const accessToken = await getTokenSilently();
		return protocolOptions.getProtocolDashboardOptions(
			moduleNavigation.protocolId,
			userAuth.currentClientId,
			accessToken,
		);
	};

	const generateStandardTrialHeatmapExcelMutator = (options?: { onSuccess?: () => void; onError?: () => void }) =>
		useMutation({
			mutationFn: (vars: typeof dataDownload) => generateStandardTrialHeatmapExcel(vars),
			onSuccess: (data, { id, exclusions }) => {
				toast.success("Data download was successful!");
				queryClient.invalidateQueries({ queryKey: ["heatmap standard export", id] });
				options?.onSuccess?.();
				const fileName = exclusions
					? `${localEntityName}_StandardTrialData_${moment().format("YYYYMMDD_hhmma")}.xlsx`
					: `${localEntityName}_StandardTrialData_${moment().format("YYYYMMDD_hhmma")}_Without_Exclusions.xlsx`;
				excelGenerationFunctions.downloadExcelFile(data, fileName, dataDownload.isForCSV);
			},
			onError: (error) => {
				toast.error("Data download failed.");
				console.error(error);
				options?.onError?.();
			},
		});

	const generateStandardProtocolHeatmapExcelMutator = (options?: { onSuccess?: () => void; onError?: () => void }) =>
		useMutation({
			mutationFn: (vars: typeof dataDownload) => generateStandardProtocolHeatmapExcel(vars),
			onSuccess: (data, { id, exclusions }) => {
				toast.success("Data download was successful!");
				queryClient.invalidateQueries({ queryKey: ["heatmap protocol standard export", id] });
				options?.onSuccess?.();
				const fileName = `${localEntityName}_StandardProtocolData_${moment().format("YYYYMMDD_hhmma")}.xlsx`;

				_.map(data, (trial) => {
					trial.trialName = `${trial.trialName}_${trial.cooperatorName}_StandardTrialData_${moment().format(
						"YYYYMMDD_hhmma",
					)}${!exclusions ? "_Without_Exclusions" : ""}.xlsx`;
				});

				if (!exclusions) {
					excelGenerationFunctions.downloadZippedExcelFile(data, fileName.split(".")[0] + "_Without_Exclusions.xlsx");
				} else {
					excelGenerationFunctions.downloadZippedExcelFile(data, fileName);
				}
			},
			onError: (error) => {
				toast.error("Data download failed.");
				console.error(error);
				options?.onError?.();
			},
		});

	const generateStackedTrialHeatmapExcelMutator = (options?: { onSuccess?: () => void; onError?: () => void }) =>
		useMutation({
			mutationFn: (vars: typeof dataDownload) => generateStackedTrialHeatmapExcel(vars),
			onSuccess: (data, { id }) => {
				toast.success("Data download was successful!");
				queryClient.invalidateQueries({ queryKey: ["heatmap universal export", id] });
				options?.onSuccess?.();
				const fileName = dataDownload.isForCSV
					? `${localEntityName}_UniversalTrialData_${moment().format("YYYYMMDD_hhmma")}.csv`
					: `${localEntityName}_UniversalTrialData_${moment().format("YYYYMMDD_hhmma")}.xlsx`;
				excelGenerationFunctions.downloadExcelFile(data, fileName, dataDownload.isForCSV);
				resetInputs();
			},
			onError: (error) => {
				toast.error("Data download failed.");
				console.error(error);
				options?.onError?.();
			},
		});

	const generateTabbedTrialHeatmapExcelMutator = (options?: { onSuccess?: () => void; onError?: () => void }) =>
		useMutation({
			mutationFn: (vars: typeof dataDownload) => generateTabbedTrialHeatmapExcel(vars),
			onSuccess: (data, { id }) => {
				toast.success("Data download was successful!");
				queryClient.invalidateQueries({ queryKey: ["heatmap tabbed export", id] });
				options?.onSuccess?.();
				const fileName = dataDownload.isForCSV
					? `${localEntityName}_TabbedTrialData_${moment().format("YYYYMMDD_hhmma")}.csv`
					: `${localEntityName}_TabbedTrialData_${moment().format("YYYYMMDD_hhmma")}.xlsx`;
				excelGenerationFunctions.downloadExcelFile(data, fileName, dataDownload.isForCSV);
				resetInputs();
			},
			onError: (error) => {
				toast.error("Data download failed.");
				console.error(error);
				options?.onError?.();
			},
		});

	const generatePlotGPSTrialCoordsExcelMutator = (options?: { onSuccess?: () => void; onError?: () => void }) =>
		useMutation({
			mutationFn: (vars: typeof dataDownload) => generatePlotGPSTrialCoordsExcel(vars),
			onSuccess: (data, { id }) => {
				toast.success("Data download was successful!");
				queryClient.invalidateQueries({ queryKey: ["gps plot trial export", id] });
				options?.onSuccess?.();
				const fileName = `${localEntityName}_GPSPlotTrialCoordinateData_${moment().format("YYYYMMDD_hhmma")}.xlsx`;
				excelGenerationFunctions.downloadExcelFile(data, fileName, dataDownload.isForCSV);
				resetInputs();
			},
			onError: (error) => {
				toast.error("Data download failed.");
				console.error(error);
				options?.onError?.();
			},
		});

	const generateStackedProtocolHeatmapExcelMutator = (options?: { onSuccess?: () => void; onError?: () => void }) =>
		useMutation({
			mutationFn: (vars: typeof dataDownload) => generateStackedProtocolHeatmapExcel(vars),
			onSuccess: (data, { id }) => {
				toast.success("Data download was successful!");
				queryClient.invalidateQueries({ queryKey: ["heatmap protocol universal export", id] });
				options?.onSuccess?.();
				const fileName = dataDownload.isForCSV
					? `${localEntityName}_UniversalProtocolData_${moment().format("YYYYMMDD_hhmma")}.csv`
					: `${localEntityName}_UniversalProtocolData_${moment().format("YYYYMMDD_hhmma")}.xlsx`;
				excelGenerationFunctions.downloadExcelFile(data, fileName, dataDownload.isForCSV);
			},
			onError: (error) => {
				toast.error("Data download failed.");
				console.error(error);
				options?.onError?.();
			},
		});

	const generateTabbedProtocolHeatmapExcelMutator = (options?: { onSuccess?: () => void; onError?: () => void }) =>
		useMutation({
			mutationFn: (vars: typeof dataDownload) => generateTabbedProtocolHeatmapExcel(vars),
			onSuccess: (data, { id }) => {
				toast.success("Data download was successful!");
				queryClient.invalidateQueries({ queryKey: ["heatmap protocol tabbed export", id] });
				options?.onSuccess?.();
				const fileName = `${localEntityName}_TabbedProtocolData_${moment().format("YYYYMMDD_hhmma")}.xlsx`;
				excelGenerationFunctions.downloadExcelFile(data, fileName, dataDownload.isForCSV);
			},
			onError: (error) => {
				toast.error("Data download failed.");
				console.error(error);
				options?.onError?.();
			},
		});

	const generatePlotGPSProtocolCoordsExcelMutator = (options?: { onSuccess?: () => void; onError?: () => void }) =>
		useMutation({
			mutationFn: (vars: typeof dataDownload) => generatePlotGPSProtocolCoordsExcel(vars),
			onSuccess: (data, { id }) => {
				toast.success("Data download was successful!");
				queryClient.invalidateQueries({ queryKey: ["heatmap protocol tabbed export", id] });
				options?.onSuccess?.();
				const fileName = `${localEntityName}_GPSPlotProtocolCoordinateData_${moment().format("YYYYMMDD_hhmma")}.xlsx`;
				excelGenerationFunctions.downloadExcelFile(data, fileName, dataDownload.isForCSV);
			},
			onError: (error) => {
				toast.error("Data download failed.");
				console.error(error);
				options?.onError?.();
			},
		});

	const generateStandardFieldHeatmapExcelMutator = (options?: { onSuccess?: () => void; onError?: () => void }) =>
		useMutation({
			mutationFn: (vars: typeof dataDownload) => generateStandardFieldHeatmapExcel(vars),
			onSuccess: (data, { id }) => {
				toast.success("Data download was successful!");
				queryClient.invalidateQueries({ queryKey: ["heatmap standard field export", id] });
				options?.onSuccess?.();
				const fileName = `${localEntityName}_StandardFieldData_${moment().format("YYYYMMDD_hhmma")}.xlsx`;
				excelGenerationFunctions.downloadExcelFile(data, fileName, dataDownload.isForCSV);
			},
			onError: (error) => {
				toast.error("Data download failed.");
				console.error(error);
				options?.onError?.();
			},
		});

	const generateStackedFieldHeatmapExcelMutator = (options?: { onSuccess?: () => void; onError?: () => void }) =>
		useMutation({
			mutationFn: (vars: typeof dataDownload) => generateStackedFieldHeatmapExcel(vars),
			onSuccess: (data, { id }) => {
				toast.success("Data download was successful!");
				queryClient.invalidateQueries({ queryKey: ["heatmap universal field export", id] });
				options?.onSuccess?.();
				const fileName = dataDownload.isForCSV
					? `${localEntityName}_UniversalFieldData_${moment().format("YYYYMMDD_hhmma")}.csv`
					: `${localEntityName}_UniversalFieldData_${moment().format("YYYYMMDD_hhmma")}.xlsx`;
				excelGenerationFunctions.downloadExcelFile(data, fileName, dataDownload.isForCSV);
				resetInputs();
			},
			onError: (error) => {
				toast.error("Data download failed.");
				console.error(error);
				options?.onError?.();
			},
		});

	const generateTabbedFieldHeatmapExcelMutator = (options?: { onSuccess?: () => void; onError?: () => void }) =>
		useMutation({
			mutationFn: (vars: typeof dataDownload) => generateTabbedFieldHeatmapExcel(vars),
			onSuccess: (data, { id }) => {
				toast.success("Data download was successful!");
				queryClient.invalidateQueries({ queryKey: ["heatmap tabbed field export", id] });
				options?.onSuccess?.();
				const fileName = `${localEntityName}_TabbedFieldData_${moment().format("YYYYMMDD_hhmma")}.xlsx`;
				excelGenerationFunctions.downloadExcelFile(data, fileName, dataDownload.isForCSV);
				resetInputs();
			},
			onError: (error) => {
				toast.error("Data download failed.");
				console.error(error);
				options?.onError?.();
			},
		});

	const generatePlotGPSFieldCoordsExcelMutator = (options?: { onSuccess?: () => void; onError?: () => void }) =>
		useMutation({
			mutationFn: (vars: typeof dataDownload) => generatePlotGPSFieldCoordsExcel(vars),
			onSuccess: (data, { id }) => {
				toast.success("Data download was successful!");
				queryClient.invalidateQueries({ queryKey: ["gps plot field export", id] });
				options?.onSuccess?.();
				const fileName = `${localEntityName}_GPSPlotFieldCoordinateData_${moment().format("YYYYMMDD_hhmma")}.xlsx`;
				excelGenerationFunctions.downloadExcelFile(data, fileName, dataDownload.isForCSV);
				resetInputs();
			},
			onError: (error) => {
				toast.error("Data download failed.");
				console.error(error);
				options?.onError?.();
			},
		});

	const getProtocolDashboardOptionsMutator = (options?: { onSuccess?: () => void; onError?: () => void }) =>
		useMutation({
			mutationFn: () => getProtocolDashboardOptions(),
			onSuccess: (data) => {
				queryClient.invalidateQueries({ queryKey: ["protocol dashboard options", id] });
				options?.onSuccess?.();
				setDashboardOptions(data.data);
			},
			onError: (error) => {
				toast.error("Failed to get protocol dashboard options.");
				console.error(error);
				options?.onError?.();
			},
		});

	const { mutate: stackedTrialMutate } = generateStackedTrialHeatmapExcelMutator();
	const { mutate: stackedProtocolMutate } = generateStackedProtocolHeatmapExcelMutator();
	const { mutate: standardTrialMutate } = generateStandardTrialHeatmapExcelMutator();
	const { mutate: tabbedTrialMutate } = generateTabbedTrialHeatmapExcelMutator();
	const { mutate: tabbedProtocolMutate } = generateTabbedProtocolHeatmapExcelMutator();
	const { mutate: standardProtocolMutate } = generateStandardProtocolHeatmapExcelMutator();
	const { mutate: stackedFieldMutate } = generateStackedFieldHeatmapExcelMutator();
	const { mutate: standardFieldMutate } = generateStandardFieldHeatmapExcelMutator();
	const { mutate: tabbedFieldMutate } = generateTabbedFieldHeatmapExcelMutator();
	const { mutate: protocolDashboardOptionsMutate } = getProtocolDashboardOptionsMutator();
	const { mutate: plotGPSTrialCoordsMutate } = generatePlotGPSTrialCoordsExcelMutator();
	const { mutate: plotGPSFieldCoordsMutate } = generatePlotGPSFieldCoordsExcelMutator();
	const { mutate: plotGPSProtocolCoordsMutate } = generatePlotGPSProtocolCoordsExcelMutator();

	const exportData = () => {
		toast.info("Data Export Started");
		setOpen(false);
		const fileFormatMatch = fileFormatOptions.find((x) => x.value == fileFormat);
		const dataUnitMatch = dataUnitOptions.find((x) => x.value == dataUnit);
		const dataContentMatch = dataContentOptions.find((x) => x.value == dataContent);
		const quantifiedRegionMatch = quantifiedRegionOptions.find((x) => x.value == quantifiedRegion);

		dataDownload.id = id;
		dataDownload.includeSubplots = dataUnitMatch.includeSubplots;
		dataDownload.quantifiedRegion = quantifiedRegionMatch.value;
		dataDownload.isForAdmin = dataContentMatch.isForAdmin;
		dataDownload.exclusions = fileFormatMatch.exclusions;
		dataDownload.isForCSV = fileFormatMatch.isForCSV;

		if (type == "Trial") {
			if (fileFormatMatch.type == ExportTypes.Universal) {
				stackedTrialMutate(dataDownload);
			} else if (fileFormatMatch.type == ExportTypes.Standard) {
				standardTrialMutate(dataDownload);
			} else if (fileFormatMatch.type == ExportTypes.Tabbed) {
				tabbedTrialMutate(dataDownload);
			} else if (fileFormatMatch.type == ExportTypes.PlotGPSCoords) {
				plotGPSTrialCoordsMutate(dataDownload);
			}
		} else if (type == "Field") {
			if (fileFormatMatch.type == ExportTypes.Universal) {
				stackedFieldMutate(dataDownload);
			} else if (fileFormatMatch.type == ExportTypes.Standard) {
				standardFieldMutate(dataDownload);
			} else if (fileFormatMatch.type == ExportTypes.Tabbed) {
				tabbedFieldMutate(dataDownload);
			} else if (fileFormatMatch.type == ExportTypes.PlotGPSCoords) {
				plotGPSFieldCoordsMutate(dataDownload);
			}
		} else if (type == "Protocol") {
			if (fileFormatMatch.type == ExportTypes.Universal) {
				stackedProtocolMutate(dataDownload);
			} else if (fileFormatMatch.type == ExportTypes.Standard) {
				standardProtocolMutate(dataDownload);
			} else if (fileFormatMatch.type == ExportTypes.Tabbed) {
				tabbedProtocolMutate(dataDownload);
			} else if (fileFormatMatch.type == ExportTypes.PlotGPSCoords) {
				plotGPSProtocolCoordsMutate(dataDownload);
			}
		}
		return true;
	};

	return (
		<Modal open={open} style={{ width: "30%", maxHeight: "85%" }}>
			<Modal.Header>{`Download ${type} Data for ${localEntityName}`}</Modal.Header>
			<Modal.Content>
				<Form style={{ padding: "1em" }}>
					<div style={{ display: "flex", flexDirection: "column", paddingRight: "1em" }}>
						<Form.Field width={16} style={{ flexGrow: 1 }}>
							<Form.Select
								label="File Format"
								options={fileFormatOptions}
								value={fileFormat}
								onChange={(_e, { value }) => {
									setFileFormat(value as string);
									filterDataUnitOptions(value);
									filterQuantifiedRegionOptions(value);
									filterDataContentOptions(value);
								}}
							/>
						</Form.Field>
						<Form.Field width={16} style={{ flexGrow: 1 }}>
							<Form.Select
								label="Data Unit"
								options={filteredDataUnitOptions}
								value={dataUnit}
								onChange={(_e, { value }) => {
									setDataUnit(value as string);
									filterQuantifiedRegionOptions(value);
								}}
							/>
						</Form.Field>
						<Form.Field width={16} style={{ flexGrow: 1 }}>
							<Form.Select
								label="Quantified Region"
								options={filteredQuantifiedRegionOptions}
								value={quantifiedRegion}
								onChange={(_e, { value }) => {
									setQuantifiedRegion(value as string);
								}}
							/>
						</Form.Field>
						<Form.Field width={16} style={{ flexGrow: 1 }}>
							<Form.Select
								label="Data Content"
								options={filteredDataContentOptions}
								value={dataContent}
								onChange={(_e, { value }) => {
									setDataContent(value as string);
								}}
							/>
						</Form.Field>
					</div>
				</Form>
			</Modal.Content>
			<Modal.Actions>
				<Button
					content="Cancel"
					onClick={() => {
						setOpen(false);
						resetInputs();
					}}
				/>
				<Button primary content="Export" onClick={exportData} />
			</Modal.Actions>
		</Modal>
	);
};

export default HeatmapExportModal;
